import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Text,
    Box,
    Stack,
    Heading,
    Grid,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'

import EmailSignUpForm from './partials/email-signup-form'
import ContentAsset from '../../../content-asset'

export const EmailSignUp = ({
    enableBirthdaySelection = 'yes',
    backgroundColor = 'white',
    desktopAlignment = 'center',

    largeHeadlineText,
    largeHeadlineFontFamily = 'heading',
    largeHeadlineColor = 'black',
    largeHeadlineTag = 'p',

    smallHeadlineText,
    smallHeadlineFontFamily = 'heading',
    smallHeadlineColor = 'black',
    smallHeadlineTag = 'h2',

    bodyText,
    bodyColor = 'black.100',
    bodyTag = 'p',

    buttonText,
    buttonTextColor,
    buttonBgColor
}) => {
    const [showSuccessMessage, setShowSuccessMessage] = useState()
    const handleFormSuccess = () => {
        setShowSuccessMessage(true)
    }
    const isCenterAligned = desktopAlignment === 'center'
    const props = {
        bodyColor,
        isCenterAligned,
        enableBirthdaySelection,
        buttonText,
        buttonTextColor,
        buttonBgColor,
        onSuccess: handleFormSuccess
    }

    // layout
    const containerStyle = {
        templateColumns: {base: '1fr', lg: isCenterAligned ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)'},
        gridTemplateRows: {base: 'repeat(2, 1fr)', lg: '1fr'},
        gap: {base: '40px', lg: isCenterAligned ? '40px' : '60px'},
        maxWidth: isCenterAligned ? '700px' : '1000px',
        p: '0',
        margin: 'auto',
        textAlign: {base: 'center', md: desktopAlignment},
        backgroundColor: backgroundColor
    }

    const mainContainer = {
        backgroundColor: backgroundColor,
        p: {base: '40px 15px', md: '40px'},
        textAlign: 'center',
        color: bodyColor
    }

    // headings
    const largeHeadlineProps = {
        margin: '0',
        fontSize: {base: '34px', '2md': '36px', lg: '42px'},
        letterSpacing: {base: '0.17px', lg: '0.21px'},
        fontFamily: largeHeadlineFontFamily || 'caslon',
        fontWeight: '400',
        color: largeHeadlineColor
    }

    const smallHeadlineProps = {
        margin: '0',
        fontSize: {base: '29px', lg: '34px'},
        letterSpacing: {base: '0.145px', xs: '0.17px'},
        fontFamily: smallHeadlineFontFamily || 'caslon',
        fontWeight: '400',
        color: smallHeadlineColor
    }

    const bodyProps = {
        fontFamily: 'inter',
        color: bodyColor,
        letterSpacing: '0.085px',
        fontWeight: '300',
        fontSize: {base: '14px', lg: '17px'},
        lineHeight: '26px'
    }

    const successSkeleton = () => {
        const style = {
            maxWidth: '700px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            margin: 'auto'
        }

        return (
            <Box {...style}>
                <Skeleton
                    width={{base: '50%', lg: '40%'}}
                    height="42px"
                    marginBottom="10px"
                    marginLeft={{base: 'auto', lg: '0'}}
                    marginRight={{base: 'auto', lg: '0'}}
                />
                <Skeleton
                    width={{base: '70%', lg: '100%'}}
                    height="20px"
                    marginBottom="10px"
                    marginLeft={{base: 'auto', lg: '0'}}
                    marginRight={{base: 'auto', lg: '0'}}
                />
                <Skeleton
                    width="30%"
                    height="20px"
                    marginLeft={{base: 'auto', lg: '0'}}
                    marginRight={{base: 'auto', lg: '0'}}
                    display={{base: 'block', lg: 'none'}}
                />
            </Box>
        )
    }

    return (
        <Box {...mainContainer}>
            {showSuccessMessage && (
                <Box
                    sx={{
                        '& h2': {
                            lineHeight: 'normal',
                            fontWeight: '400',
                            fontFamily: 'heading',
                            fontSize: {base: '34px', md: '42px'},
                            marginBottom: '10px',
                            letterSpacing: {base: '0.17px', md: '0.21px'}
                        },
                        '& p': {
                            lineHeight: 'normal',
                            fontWeight: '300',
                            fontFamily: 'inter',
                            fontSize: '17px'
                        }
                    }}
                >
                    <ContentAsset
                        contentAssetId={'email-signup-success-message'}
                        skeleton={successSkeleton}
                    />
                </Box>
            )}

            {!showSuccessMessage && (
                <Grid {...containerStyle}>
                    <Stack gap="10px" maxWidth="700px" margin="auto">
                        {largeHeadlineText && (
                            <Heading as={largeHeadlineTag} {...largeHeadlineProps}>
                                {largeHeadlineText}
                            </Heading>
                        )}
                        {smallHeadlineText && (
                            <Heading as={smallHeadlineTag} {...smallHeadlineProps}>
                                {smallHeadlineText}
                            </Heading>
                        )}
                        {bodyText && (
                            <Text as={bodyTag} {...bodyProps}>
                                {bodyText}
                            </Text>
                        )}
                    </Stack>
                    <Stack gap={'40px'}>
                        {/* FORM */}
                        <EmailSignUpForm {...props} />
                    </Stack>
                </Grid>
            )}
        </Box>
    )
}

EmailSignUp.propTypes = {
    desktopAlignment: PropTypes.string,
    backgroundColor: PropTypes.string,
    enableBirthdaySelection: PropTypes.string,

    largeHeadlineText: PropTypes.string,
    largeHeadlineFontFamily: PropTypes.string,
    largeHeadlineColor: PropTypes.string,
    largeHeadlineTag: PropTypes.string,

    smallHeadlineText: PropTypes.string,
    smallHeadlineFontFamily: PropTypes.string,
    smallHeadlineColor: PropTypes.string,
    smallHeadlineTag: PropTypes.string,

    bodyText: PropTypes.string,
    bodyColor: PropTypes.string,
    bodyTag: PropTypes.string,

    buttonText: PropTypes.string,
    buttonTextColor: PropTypes.string,
    buttonBgColor: PropTypes.string
}

export default EmailSignUp
